import { useState } from 'react';
import { createPortal } from 'react-dom';
import { MdClose } from 'react-icons/md';
import { buildExportRequest, downloadExport, exportDocument } from '../../../services/exportService';
import styles from './ExportModal.module.css';

const ExportModal = ({ isOpen, onClose, exportData = {} }) => {
  const [selectedFormat, setSelectedFormat] = useState('html');
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [includeGlossary, setIncludeGlossary] = useState(true);
  const [includeQuote, setIncludeQuote] = useState(true);
  const [includeCitation, setIncludeCitation] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  if (!isOpen) return null;

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const requestData = buildExportRequest(exportData, {
        selectedFormat,
        includeMetadata,
        includeGlossary,
        includeQuote,
        includeCitation,
      });

      const content = await exportDocument(requestData);
      downloadExport(content, selectedFormat, exportData.filename || 'ai-summary');
      onClose();
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const modal = (
    <div className={styles.modalOverlay} onClick={() => !isExporting && onClose()}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>Export Options</h3>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            disabled={isExporting}
          >
            <MdClose />
          </button>
        </div>

        {/* Format Selection */}
        <div className={styles.section}>
          <h4>Export Format</h4>
          <div className={styles.formatGrid}>
            {['html', 'markdown', 'plaintext', 'richtext'].map((format) => (
              <label
                key={format}
                className={`${styles.formatOption} ${selectedFormat === format ? styles.selected : ''}`}
              >
                <input
                  type="radio"
                  name="format"
                  value={format}
                  checked={selectedFormat === format}
                  onChange={(e) => setSelectedFormat(e.target.value)}
                  disabled={isExporting}
                />
                <span>
                  {format === 'html' && 'HTML'}
                  {format === 'markdown' && 'Markdown'}
                  {format === 'plaintext' && 'Plain Text'}
                  {format === 'richtext' && 'Word Document'}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Content Options */}
        <div className={styles.section}>
          <h4>Include Additional Content</h4>
          <div className={styles.checkboxGroup}>
            {exportData.metadata && (
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={includeMetadata}
                  onChange={(e) => setIncludeMetadata(e.target.checked)}
                  disabled={isExporting}
                />
                <span>Document Metadata</span>
              </label>
            )}
            {exportData.glossary && (
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={includeGlossary}
                  onChange={(e) => setIncludeGlossary(e.target.checked)}
                  disabled={isExporting}
                />
                <span>Glossary</span>
              </label>
            )}
            {exportData.quote && (
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={includeQuote}
                  onChange={(e) => setIncludeQuote(e.target.checked)}
                  disabled={isExporting}
                />
                <span>Quote</span>
              </label>
            )}
            {exportData.citation && (
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={includeCitation}
                  onChange={(e) => setIncludeCitation(e.target.checked)}
                  disabled={isExporting}
                />
                <span>Citation</span>
              </label>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className={styles.actions}>
          <button
            className={styles.cancelBtn}
            onClick={onClose}
            disabled={isExporting}
          >
            Cancel
          </button>
          <button
            className={styles.confirmBtn}
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? 'Exporting...' : 'Export'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default ExportModal;
